import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Play, BarChart, Zap, Settings, TrendingUp, Users, Phone, Activity, PieChart, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";

// Import CRM Logos
import SalesforceLogo from '@/assets/crm_logos/salesforce.png';
import HubSpotLogo from '@/assets/crm_logos/hubspot.png';
import MSDynamicsLogo from '@/assets/crm_logos/msdynamics.png';
import SAPLogo from '@/assets/crm_logos/sap.png';
import ZendeskLogo from '@/assets/crm_logos/Zendesk.png';

interface ProductDemoProps {
  productType: "voice" | "analytics" | "outbound" | "integration";
}

const ProductDemo = ({ productType }: ProductDemoProps) => {
  const { t } = useTranslation();
  const [activeMetric, setActiveMetric] = useState<string>("calls");

  const demoContent = {
    voice: {
      title: t('products.demo_voice_title'),
      tabs: [
        { 
          id: "conversation", 
          label: t('products.demo_voice_tab_conversation'), 
          icon: Phone,
          content: (
            <div className="space-y-6">
              <div className="aspect-video rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/30 overflow-hidden">
                <div className="relative w-full h-full">
                  <video 
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    poster="/assets/demos/demo-poster.svg"
                  >
                    <source src="/assets/demos/voice-ai-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="text-center">
                      <Button variant="outline" className="mb-4 hover:bg-primary hover:text-white">
                        <Play className="w-6 h-6 mr-2" />
                        {t('products.demo_voice_watch_demo')}
                      </Button>
                      <p className="text-lg font-semibold text-white">{t('products.demo_voice_real_interaction')}</p>
                      <p className="text-sm text-white/80">{t('products.demo_voice_see_ai')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-green-400">99.2%</div>
                  <div className="text-xs text-muted-foreground">{t('products.demo_voice_intent_recognition')}</div>
                </div>
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-blue-400">32ms</div>
                  <div className="text-xs text-muted-foreground">{t('products.demo_voice_avg_response')}</div>
                </div>
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-purple-400">4.9/5</div>
                  <div className="text-xs text-muted-foreground">{t('products.demo_voice_csat_score')}</div>
                </div>
              </div>
            </div>
          )
        },
        { 
          id: "features", 
          label: t('products.demo_voice_tab_features'), 
          icon: Settings,
          content: (
            <div className="space-y-4">
              {[
                { title: t('products.demo_voice_feature_multilang'), desc: t('products.demo_voice_feature_multilang_desc'), progress: 95 },
                { title: t('products.demo_voice_feature_sentiment'), desc: t('products.demo_voice_feature_sentiment_desc'), progress: 88 },
                { title: t('products.demo_voice_feature_context'), desc: t('products.demo_voice_feature_context_desc'), progress: 92 },
                { title: t('products.demo_voice_feature_handoff'), desc: t('products.demo_voice_feature_handoff_desc'), progress: 97 }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 rounded-lg glass-effect"
                >
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold">{feature.title}</span>
                    <span className="text-sm text-primary">{feature.progress}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{feature.desc}</p>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${feature.progress}%` }}
                      transition={{ duration: 1, delay: idx * 0.1 }}
                      className="h-full bg-gradient-to-r from-primary to-accent"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )
        }
      ]
    },
    analytics: {
      title: t('products.demo_analytics_title'),
      tabs: [
        { 
          id: "dashboard", 
          label: t('products.demo_analytics_tab_dashboard'), 
          icon: BarChart,
          content: (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: t('products.demo_analytics_active_calls'), value: "1,247", trend: "+24%", icon: Phone, color: "from-blue-500 to-cyan-500" },
                  { label: t('products.demo_analytics_avg_duration'), value: "5:42", trend: "-12%", icon: Clock, color: "from-purple-500 to-pink-500" },
                  { label: t('products.demo_analytics_csat_score'), value: "4.8/5", trend: "+18%", icon: Activity, color: "from-green-500 to-emerald-500" },
                  { label: t('products.demo_analytics_resolution_rate'), value: "94.2%", trend: "+7%", icon: CheckCircle, color: "from-orange-500 to-red-500" }
                ].map((metric, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="relative overflow-hidden rounded-xl glass-effect p-6 cursor-pointer group hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${metric.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                    <div className="relative z-10">
                      <div className="flex justify-between items-start mb-4">
                        <metric.icon className="w-8 h-8 text-primary" />
                        <div className={`px-2 py-1 rounded text-xs font-bold ${metric.trend.startsWith('+') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {metric.trend}
                        </div>
                      </div>
                      <div className="text-3xl font-bold mb-2">{metric.value}</div>
                      <div className="text-sm text-muted-foreground font-medium">{metric.label}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Advanced Charts Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Call Volume Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="rounded-xl glass-effect p-6"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <BarChart className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold">{t('products.demo_analytics_call_volume')}</h4>
                  </div>
                  <div className="space-y-3">
                    {[
                      { day: "Mon", value: 85, max: 120 },
                      { day: "Tue", value: 102, max: 120 },
                      { day: "Wed", value: 118, max: 120 },
                      { day: "Thu", value: 95, max: 120 },
                      { day: "Fri", value: 110, max: 120 },
                      { day: "Sat", value: 64, max: 120 },
                      { day: "Sun", value: 58, max: 120 }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-8 text-xs font-semibold text-muted-foreground">{item.day}</span>
                        <div className="flex-grow h-2 bg-background rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(item.value / item.max) * 100}%` }}
                            transition={{ duration: 1, delay: idx * 0.05 }}
                            className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                          />
                        </div>
                        <span className="w-10 text-right text-xs font-bold">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Channel Distribution */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="rounded-xl glass-effect p-6"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <Users className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold">{t('products.demo_analytics_channel_dist')}</h4>
                  </div>
                  <div className="space-y-4">
                    {[
                      { channel: t('products.demo_channel_inbound'), value: 45, color: "from-blue-500 to-blue-600" },
                      { channel: t('products.demo_channel_outbound'), value: 30, color: "from-purple-500 to-purple-600" },
                      { channel: t('products.demo_channel_ivr'), value: 15, color: "from-pink-500 to-pink-600" },
                      { channel: t('products.demo_channel_callback'), value: 10, color: "from-cyan-500 to-cyan-600" }
                    ].map((item, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">{item.channel}</span>
                          <span className="text-sm font-bold text-primary">{item.value}%</span>
                        </div>
                        <div className="h-2.5 bg-background rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${item.value}%` }}
                            transition={{ duration: 1, delay: idx * 0.1 }}
                            className={`h-full bg-gradient-to-r ${item.color}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Performance Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="rounded-xl glass-effect p-6"
              >
                <h4 className="font-semibold mb-6 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('products.demo_analytics_performance')}
                </h4>
                <div className="grid grid-cols-3 gap-6">
                  {[
                    { metric: t('products.demo_analytics_avg_wait'), value: "2m 14s", baseline: "2m 30s", improvement: "-14%" },
                    { metric: t('products.demo_analytics_abandonment'), value: "3.2%", baseline: "5.8%", improvement: "-45%" },
                    { metric: t('products.demo_analytics_fcr'), value: "87.3%", baseline: "79.5%", improvement: "+9.8%" }
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 bg-background/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">{item.metric}</p>
                      <p className="text-2xl font-bold mb-1">{item.value}</p>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">vs {item.baseline}</span>
                        <span className="text-green-400 font-bold">{item.improvement}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )
        },
        { 
          id: "insights", 
          label: t('products.demo_analytics_tab_insights'), 
          icon: Zap,
          content: (
            <div className="space-y-4">
              {[
                { 
                  insight: t('products.demo_analytics_insight_peak'), 
                  impact: t('products.demo_analytics_insight_peak_impact'),
                  severity: "high",
                  action: t('products.demo_analytics_insight_peak_action'),
                  potential_savings: t('products.demo_analytics_insight_peak_savings')
                },
                { 
                  insight: t('products.demo_analytics_insight_billing'), 
                  impact: t('products.demo_analytics_insight_billing_impact'),
                  severity: "medium",
                  action: t('products.demo_analytics_insight_billing_action'),
                  potential_savings: t('products.demo_analytics_insight_billing_savings')
                },
                { 
                  insight: t('products.demo_analytics_insight_agent'), 
                  impact: t('products.demo_analytics_insight_agent_impact'),
                  severity: "low",
                  action: t('products.demo_analytics_insight_agent_action'),
                  potential_savings: t('products.demo_analytics_insight_agent_savings')
                },
                { 
                  insight: t('products.demo_analytics_insight_wait'), 
                  impact: t('products.demo_analytics_insight_wait_impact'),
                  severity: "low",
                  action: t('products.demo_analytics_insight_wait_action'),
                  potential_savings: t('products.demo_analytics_insight_wait_savings')
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`p-6 rounded-xl glass-effect border-l-4 transition-all duration-300 hover:shadow-lg ${
                    item.severity === "high" ? "border-red-500/50" :
                    item.severity === "medium" ? "border-yellow-500/50" :
                    "border-green-500/50"
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-3 h-3 rounded-full mt-1 ${
                        item.severity === "high" ? "bg-red-500" :
                        item.severity === "medium" ? "bg-yellow-500" :
                        "bg-green-500"
                      }`} />
                      <div>
                        <p className="font-semibold text-base mb-2">{item.insight}</p>
                        <p className="text-sm text-muted-foreground">{item.impact}</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ml-4 ${
                      item.severity === "high" ? "bg-red-500/20 text-red-400" :
                      item.severity === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-green-500/20 text-green-400"
                    }`}>
                      {item.severity === "high" ? t('products.demo_analytics_severity_high') : 
                       item.severity === "medium" ? t('products.demo_analytics_severity_medium') : 
                       t('products.demo_analytics_severity_low')}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('products.demo_analytics_recommended_action')}</p>
                      <p className="font-semibold text-sm text-primary">{item.action}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">{t('products.demo_analytics_potential_savings')}</p>
                      <p className="font-semibold text-sm text-green-400">{item.potential_savings}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        }
      ]
    },
    outbound: {
      title: t('products.demo_outbound_title'),
      tabs: [
        { 
          id: "campaign", 
          label: t('products.demo_outbound_tab_campaign'), 
          icon: Zap,
          content: (
            <div className="space-y-8">
              {/* Campaign Pipeline */}
              <div className="relative">
                {[
                  { step: "1", title: t('products.demo_outbound_step1_title'), status: t('products.demo_outbound_status_complete'), count: "5,000", desc: t('products.demo_outbound_step1_desc'), progress: 100 },
                  { step: "2", title: t('products.demo_outbound_step2_title'), status: t('products.demo_outbound_status_progress'), count: "3,847", desc: t('products.demo_outbound_step2_desc'), progress: 77 },
                  { step: "3", title: t('products.demo_outbound_step3_title'), status: t('products.demo_outbound_status_scheduled'), count: "2,156", desc: t('products.demo_outbound_step3_desc'), progress: 0 },
                  { step: "4", title: t('products.demo_outbound_step4_title'), status: t('products.demo_outbound_status_pending'), count: "892", desc: t('products.demo_outbound_step4_desc'), progress: 0 }
                ].map((step, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    className="relative"
                  >
                    <div className="flex items-start gap-4 p-6 rounded-xl glass-effect border border-primary/30 hover:border-primary/60 transition-all duration-300 group">
                      <div className="w-14 h-14 rounded-full gradient-brand flex items-center justify-center text-white font-bold text-2xl shrink-0 group-hover:shadow-lg group-hover:shadow-primary/50 transition-all">
                        {step.step}
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-bold text-lg mb-1">{step.title}</div>
                            <div className="text-sm text-muted-foreground">{step.desc}</div>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ml-4 ${
                            step.status === t('products.demo_outbound_status_complete') ? "bg-green-500/20 text-green-400" :
                            step.status === t('products.demo_outbound_status_progress') ? "bg-blue-500/20 text-blue-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`}>
                            {step.status}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-xs font-bold text-primary">{step.count} leads</span>
                          <div className="flex-grow h-2 bg-background rounded-full overflow-hidden max-w-xs">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${step.progress}%` }}
                              transition={{ duration: 1, delay: idx * 0.15 }}
                              className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                            />
                          </div>
                          <span className="text-xs font-bold text-muted-foreground">{step.progress}%</span>
                        </div>
                      </div>
                    </div>
                    {idx < 3 && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.15 + 0.3 }}
                        className="absolute left-7 top-[104px] w-0.5 h-8 bg-gradient-to-b from-primary/50 to-accent/50"
                      />
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-3 gap-4 pt-4 border-t border-border"
              >
                {[
                  { label: t('products.demo_outbound_total_contacts'), value: "5,000", icon: "📊" },
                  { label: t('products.demo_outbound_qualified_rate'), value: "76.9%", icon: "✅" },
                  { label: t('products.demo_outbound_estimated_revenue'), value: "$187K", icon: "💰" }
                ].map((item, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-background/50 text-center">
                    <div className="text-2xl mb-2">{item.icon}</div>
                    <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                    <p className="text-2xl font-bold text-primary">{item.value}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          )
        },
        { 
          id: "results", 
          label: t('products.demo_outbound_tab_results'), 
          icon: TrendingUp,
          content: (
            <div className="space-y-6">
              {/* Primary Metrics */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { metric: t('products.demo_outbound_contact_rate'), value: "73.4%", comparison: "+18% vs manual", color: "from-blue-500 to-cyan-500", baseline: "62%" },
                  { metric: t('products.demo_outbound_qualified_leads'), value: "1,247", comparison: "3.2x industry avg", color: "from-green-500 to-emerald-500", baseline: "389" },
                  { metric: t('products.demo_outbound_cost_per_lead'), value: "$4.20", comparison: "-62% reduction", color: "from-purple-500 to-pink-500", baseline: "$11.10" },
                  { metric: t('products.demo_outbound_appointments'), value: "418", comparison: "+156% increase", color: "from-orange-500 to-red-500", baseline: "163" }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`p-6 rounded-xl glass-effect text-center border border-primary/20 hover:border-primary/50 transition-all group cursor-pointer`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity`} />
                    <div className="relative z-10">
                      <div className="text-sm text-muted-foreground mb-1">{item.metric}</div>
                      <div className="text-3xl font-bold mb-2 gradient-brand-text">{item.value}</div>
                      <div className="flex justify-between items-center text-xs mb-3">
                        <span className="text-muted-foreground">vs {item.baseline}</span>
                        <span className="text-green-400 font-bold">{item.comparison}</span>
                      </div>
                      <div className="h-1.5 bg-background rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.8, delay: idx * 0.1 + 0.2 }}
                          className={`h-full bg-gradient-to-r ${item.color}`}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Campaign Comparison */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="rounded-xl glass-effect p-6"
              >
                <h4 className="font-semibold mb-6">{t('products.demo_outbound_comparison_manual')}</h4>
                <div className="space-y-5">
                  {[
                    { metric: t('products.demo_outbound_time_launch'), manual: "3 weeks", automated: "24 hours", improvement: "95% faster" },
                    { metric: t('products.demo_outbound_touches_lead'), manual: "2-3", automated: "5-7", improvement: "Adaptive" },
                    { metric: t('products.demo_outbound_personalization'), manual: "Generic", automated: "Advanced AI", improvement: "Real-time" },
                    { metric: t('products.demo_outbound_followup_rate'), manual: "45%", automated: "89%", improvement: "+97%" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                      <span className="font-medium text-sm">{item.metric}</span>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{t('products.demo_outbound_manual')}</p>
                          <p className="font-semibold">{item.manual}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{t('products.demo_outbound_automated')}</p>
                          <p className="font-semibold text-primary">{item.automated}</p>
                        </div>
                        <div className="text-right w-24">
                          <p className="text-xs text-muted-foreground">{t('products.demo_outbound_improvement')}</p>
                          <p className="font-semibold text-green-400">{item.improvement}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Revenue Impact */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="rounded-xl glass-effect p-6 border-l-4 border-green-500/50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold mb-3">{t('products.demo_outbound_revenue_impact')}</h4>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">{t('products.demo_outbound_new_customers')}</p>
                      <p className="text-4xl font-bold text-green-400 mb-4">+$127,500</p>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('products.demo_outbound_appointments')}</p>
                          <p className="font-bold">418/mo</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('products.demo_outbound_avg_deal')}</p>
                          <p className="font-bold">$3,050</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('products.demo_outbound_close_rate')}</p>
                          <p className="font-bold">45%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-6xl font-bold gradient-brand-text mb-2">↑ 156%</div>
                    <p className="text-sm text-muted-foreground">{t('products.demo_outbound_vs_manual')}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )
        }
      ]
    },
    integration: {
      title: t('products.demo_integration_title'),
      tabs: [
        { 
          id: "connectors", 
          label: t('products.demo_integration_tab_connectors'), 
          icon: Settings,
          content: (
            <div className="space-y-6">
              {/* Integration Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0 }}
                className="rounded-xl glass-effect p-6 border border-primary/20"
              >
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    { label: t('products.demo_integration_connected_systems'), value: "5", icon: Users },
                    { label: t('products.demo_integration_active_sync'), value: "24/7", icon: Activity },
                    { label: t('products.demo_integration_uptime'), value: "99.98%", icon: CheckCircle },
                    { label: t('products.demo_integration_avg_sync'), value: "2.3s", icon: Clock }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center p-3 bg-background/50 rounded-lg">
                      <item.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                      <p className="font-bold text-primary">{item.value}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Connected Platforms Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[
                  { name: "Salesforce", status: t('products.demo_integration_status_connected'), sync: "2 min ago", logo: SalesforceLogo, category: "CRM", records: "12,847" },
                  { name: "HubSpot", status: t('products.demo_integration_status_connected'), sync: "5 min ago", logo: HubSpotLogo, category: "CRM", records: "8,364" },
                  { name: "Zendesk", status: t('products.demo_integration_status_connected'), sync: "1 min ago", logo: ZendeskLogo, category: "Support", records: "15,293" },
                  { name: "Microsoft Dynamics", status: t('products.demo_integration_status_connected'), sync: "3 min ago", logo: MSDynamicsLogo, category: "CRM", records: "9,156" },
                  { name: "SAP", status: t('products.demo_integration_status_connected'), sync: "7 min ago", logo: SAPLogo, category: "Enterprise", records: "24,567" }
                ].map((system, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="relative overflow-hidden rounded-2xl glass-effect p-4 lg:p-5 border border-primary/20 hover:border-primary/50 transition-all group cursor-pointer"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-3">
                        <div className="w-16 h-16 rounded-2xl bg-background/80 flex items-center justify-center shadow-inner">
                          <img 
                            src={system.logo} 
                            alt={system.name}
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-lg font-semibold leading-tight">{system.name}</p>
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${system.status === t('products.demo_integration_status_connected') ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>
                              {system.status}
                            </div>
                          </div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">{system.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{t('products.demo_integration_records')}: <span className="text-primary font-semibold">{system.records}</span></span>
                        <span>{t('products.demo_integration_last_sync')}: <span className="text-primary font-semibold">{system.sync}</span></span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Data Flow Visualization */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="rounded-xl glass-effect p-6"
              >
                <h4 className="font-semibold mb-6">{t('products.demo_integration_data_flow')}</h4>
                <div className="flex items-center justify-between">
                  {[
                    { label: t('products.demo_integration_inbound_calls'), value: "1,247/hr", icon: Phone },
                    { label: t('products.demo_integration_crm_update'), value: "100%", icon: CheckCircle },
                    { label: t('products.demo_integration_analytics'), value: "Real-time", icon: BarChart },
                    { label: t('products.demo_integration_reporting'), value: "Live", icon: TrendingUp }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="text-center">
                        <item.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className="font-bold text-primary">{item.value}</p>
                      </div>
                      {idx < 3 && (
                        <div className="text-2xl text-primary/50 font-bold">→</div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )
        },
        {
          id: "workflows",
          label: t('products.demo_integration_tab_workflows'),
          icon: Zap,
          content: (
            <div className="space-y-4">
              {[
                {
                  name: t('products.demo_integration_workflow_sync'),
                  description: t('products.demo_integration_workflow_sync_desc'),
                  triggers: t('products.demo_integration_trigger_completion'),
                  systems: ["Salesforce", "HubSpot", "Zendesk"],
                  status: t('products.demo_integration_status_active'),
                  executions: "12,847",
                  success_rate: "99.8%"
                },
                {
                  name: t('products.demo_integration_workflow_qualification'),
                  description: t('products.demo_integration_workflow_qualification_desc'),
                  triggers: t('products.demo_integration_trigger_lead'),
                  systems: ["Custom API", "Microsoft Dynamics"],
                  status: t('products.demo_integration_status_active'),
                  executions: "3,247",
                  success_rate: "98.9%"
                },
                {
                  name: t('products.demo_integration_workflow_escalation'),
                  description: t('products.demo_integration_workflow_escalation_desc'),
                  triggers: t('products.demo_integration_trigger_issue'),
                  systems: ["Zendesk", "SAP"],
                  status: t('products.demo_integration_status_active'),
                  executions: "847",
                  success_rate: "99.4%"
                },
                {
                  name: t('products.demo_integration_workflow_analytics'),
                  description: t('products.demo_integration_workflow_analytics_desc'),
                  triggers: t('products.demo_integration_trigger_hourly'),
                  systems: ["Salesforce", "Custom API"],
                  status: t('products.demo_integration_status_active'),
                  executions: "720",
                  success_rate: "99.9%"
                }
              ].map((workflow, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="rounded-xl glass-effect p-6 border border-primary/20 hover:border-primary/50 transition-all"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h5 className="font-bold text-base mb-1">{workflow.name}</h5>
                      <p className="text-sm text-muted-foreground">{workflow.description}</p>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400 whitespace-nowrap ml-4">
                      {workflow.status}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('products.demo_integration_trigger')}</p>
                      <p className="text-sm font-semibold">{workflow.triggers}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('products.demo_integration_connected_systems_label')}</p>
                      <div className="flex flex-wrap gap-1">
                        {workflow.systems.map((sys, sidx) => (
                          <span key={sidx} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                            {sys}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">{t('products.demo_integration_executions')}</span>
                      <span className="text-sm font-bold text-primary">{workflow.executions}</span>
                    </div>
                    <div className="flex justify-between text-right">
                      <span className="text-xs text-muted-foreground">{t('products.demo_integration_success_rate')}</span>
                      <span className="text-sm font-bold text-green-400">{workflow.success_rate}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        },
        {
          id: "analytics",
          label: t('products.demo_integration_tab_analytics'),
          icon: BarChart,
          content: (
            <div className="space-y-6">
              {/* Health Metrics */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { metric: t('products.demo_integration_system_health'), value: "99.98%", status: t('products.demo_integration_status_excellent'), color: "text-green-400", icon: CheckCircle },
                  { metric: t('products.demo_integration_data_accuracy'), value: "99.95%", status: t('products.demo_integration_status_excellent'), color: "text-green-400", icon: CheckCircle },
                  { metric: t('products.demo_integration_avg_latency'), value: "248ms", status: t('products.demo_integration_status_optimal'), color: "text-blue-400", icon: Clock },
                  { metric: t('products.demo_integration_processing_speed'), value: "1,247/sec", status: t('products.demo_integration_status_high'), color: "text-purple-400", icon: Activity }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="rounded-xl glass-effect p-4 border border-primary/20 text-center"
                  >
                    <item.icon className={`w-6 h-6 mx-auto mb-2 ${item.color}`} />
                    <p className="text-xs text-muted-foreground mb-2">{item.metric}</p>
                    <p className={`text-2xl font-bold mb-1 ${item.color}`}>{item.value}</p>
                    <p className="text-xs text-muted-foreground">{item.status}</p>
                  </motion.div>
                ))}
              </div>

              {/* System Reliability */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="rounded-xl glass-effect p-6"
              >
                <h4 className="font-semibold mb-6">{t('products.demo_integration_reliability')}</h4>
                <div className="space-y-3">
                  {[
                    { system: "Salesforce", uptime: 99.98 },
                    { system: "HubSpot", uptime: 99.95 },
                    { system: "Zendesk", uptime: 100 },
                    { system: "Microsoft Dynamics", uptime: 99.92 },
                    { system: "SAP", uptime: 99.88 },
                    { system: "Custom API", uptime: 99.99 }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-32 text-sm font-medium truncate">{item.system}</span>
                      <div className="flex-grow h-2 bg-background rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${item.uptime}%` }}
                          transition={{ duration: 0.8, delay: idx * 0.05 }}
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-500"
                        />
                      </div>
                      <span className="w-12 text-right text-sm font-bold text-green-400">{item.uptime}%</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Data Sync Statistics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="rounded-xl glass-effect p-6 border-l-4 border-blue-500/50"
              >
                <h4 className="font-semibold mb-4">{t('products.demo_integration_sync_stats')}</h4>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: t('products.demo_integration_records_synced'), value: "94,227", subtitle: t('products.demo_integration_this_month'), icon: CheckCircle },
                    { label: t('products.demo_integration_sync_errors'), value: "3", subtitle: t('products.demo_integration_auto_resolved'), icon: AlertCircle },
                    { label: t('products.demo_integration_data_integrity'), value: "100%", subtitle: t('products.demo_integration_validated'), icon: CheckCircle }
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 bg-background/50 rounded-lg text-center">
                      <item.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-2xl font-bold mb-1 text-primary">{item.value}</p>
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">{item.subtitle}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )
        }
      ]
    }
  };

  const demo = demoContent[productType];

  return (
    <Card className="p-8 md:p-12 glass-effect border-primary/30 bg-gradient-to-br from-background/50 to-background">
      <div className="flex items-center gap-3 mb-8">
        <Play className="w-10 h-10 text-primary" />
        <h3 className="text-3xl font-bold">{demo.title}</h3>
      </div>

      <Tabs defaultValue={demo.tabs[0].id} className="w-full">
        <TabsList className="grid w-full mb-8" style={{ gridTemplateColumns: `repeat(${demo.tabs.length}, 1fr)` }}>
          {demo.tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {demo.tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>

      <div className="mt-8 pt-8 border-t border-border">
        <div className="flex gap-4">
          <Link to="/contact" className="flex-1">
            <Button className="w-full gradient-brand text-white hover:opacity-90">
              {t('products.demo_request_live')}
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default ProductDemo;
